/**
 * F-077 — Texas Security Audit Skills
 *
 * Security audit / self-assessment skill tooling for the Texas compliance lane.
 * Provides a structured self-assessment framework that evaluates an org's
 * security posture across CCPA, Texas §521.053, and general US data-breach
 * readiness domains.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type AuditDomain =
  | 'DATA_INVENTORY'
  | 'ACCESS_CONTROLS'
  | 'ENCRYPTION'
  | 'INCIDENT_RESPONSE'
  | 'VENDOR_MANAGEMENT'
  | 'EMPLOYEE_TRAINING'
  | 'DATA_RETENTION'
  | 'BREACH_NOTIFICATION';

export type AuditStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE';
export type FindingSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type ControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'NOT_APPLICABLE';

export interface AuditControl {
  id: string;
  domain: AuditDomain;
  title: string;
  description: string;
  statutoryRef?: string;
  status: ControlStatus;
  evidence?: string;
  notes?: string;
}

export interface AuditFinding {
  id: string;
  controlId: string;
  severity: FindingSeverity;
  title: string;
  description: string;
  recommendation: string;
  detectedAt: string;
  resolvedAt?: string;
}

export interface SecurityAudit {
  id: string;
  title: string;
  status: AuditStatus;
  createdAt: string;
  updatedAt: string;
  controls: AuditControl[];
  findings: AuditFinding[];
  score: number; // 0-100
}

// ─── Default Control Catalog ─────────────────────────────────────────────────

const CONTROL_CATALOG: Omit<AuditControl, 'status' | 'evidence' | 'notes'>[] = [
  { id: 'DI-001', domain: 'DATA_INVENTORY', title: 'Personal Data Inventory', description: 'Maintain a comprehensive inventory of all personal data collected, stored, and processed.', statutoryRef: 'CCPA §1798.100' },
  { id: 'DI-002', domain: 'DATA_INVENTORY', title: 'Data Flow Mapping', description: 'Map data flows for all personal information across systems and third parties.' },
  { id: 'AC-001', domain: 'ACCESS_CONTROLS', title: 'Role-Based Access Control', description: 'Implement role-based access controls for all systems containing personal data.' },
  { id: 'AC-002', domain: 'ACCESS_CONTROLS', title: 'Multi-Factor Authentication', description: 'Enforce MFA for administrative and privileged access.' },
  { id: 'EN-001', domain: 'ENCRYPTION', title: 'Data-at-Rest Encryption', description: 'Encrypt all personal data at rest using AES-256 or equivalent.' },
  { id: 'EN-002', domain: 'ENCRYPTION', title: 'Data-in-Transit Encryption', description: 'Enforce TLS 1.2+ for all data in transit.' },
  { id: 'IR-001', domain: 'INCIDENT_RESPONSE', title: 'Incident Response Plan', description: 'Maintain a documented incident response plan with defined roles and procedures.', statutoryRef: 'Texas §521.053' },
  { id: 'IR-002', domain: 'INCIDENT_RESPONSE', title: 'Breach Notification Process', description: 'Document breach notification process meeting CCPA 45-day and Texas 60-day requirements.', statutoryRef: 'CCPA §1798.150 / Texas §521.053' },
  { id: 'VM-001', domain: 'VENDOR_MANAGEMENT', title: 'Vendor Security Assessment', description: 'Assess security posture of all third-party vendors handling personal data.' },
  { id: 'ET-001', domain: 'EMPLOYEE_TRAINING', title: 'Security Awareness Training', description: 'Annual security awareness training for all employees handling personal data.' },
  { id: 'DR-001', domain: 'DATA_RETENTION', title: 'Data Retention Policy', description: 'Define and enforce data retention and deletion schedules.', statutoryRef: 'CCPA §1798.105' },
  { id: 'BN-001', domain: 'BREACH_NOTIFICATION', title: 'CCPA Breach Notification', description: 'Ability to notify affected consumers within 45 days of discovery.', statutoryRef: 'CCPA §1798.150' },
  { id: 'BN-002', domain: 'BREACH_NOTIFICATION', title: 'Texas Breach Notification', description: 'Ability to notify affected individuals within 60 days per Texas Business & Commerce Code.', statutoryRef: 'Texas §521.053' },
];

// ─── Engine ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'security_audits';

function loadAudits(): SecurityAudit[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveAudits(audits: SecurityAudit[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(audits));
}

export function createAudit(title: string): SecurityAudit {
  const now = new Date().toISOString();
  const audit: SecurityAudit = {
    id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title,
    status: 'NOT_STARTED',
    createdAt: now,
    updatedAt: now,
    controls: CONTROL_CATALOG.map((c) => ({ ...c, status: 'NOT_IMPLEMENTED' as ControlStatus })),
    findings: [],
    score: 0,
  };
  const audits = loadAudits();
  audits.push(audit);
  saveAudits(audits);
  return audit;
}

export function getAudits(): SecurityAudit[] {
  return loadAudits();
}

export function getAudit(id: string): SecurityAudit | undefined {
  return loadAudits().find((a) => a.id === id);
}

export function updateControlStatus(
  auditId: string,
  controlId: string,
  status: ControlStatus,
  evidence?: string,
  notes?: string,
): SecurityAudit | undefined {
  const audits = loadAudits();
  const audit = audits.find((a) => a.id === auditId);
  if (!audit) return undefined;

  const control = audit.controls.find((c) => c.id === controlId);
  if (!control) return undefined;

  control.status = status;
  if (evidence !== undefined) control.evidence = evidence;
  if (notes !== undefined) control.notes = notes;

  audit.updatedAt = new Date().toISOString();
  audit.status = 'IN_PROGRESS';
  audit.score = calculateScore(audit);

  saveAudits(audits);
  return audit;
}

export function addFinding(auditId: string, finding: Omit<AuditFinding, 'id' | 'detectedAt'>): SecurityAudit | undefined {
  const audits = loadAudits();
  const audit = audits.find((a) => a.id === auditId);
  if (!audit) return undefined;

  audit.findings.push({
    ...finding,
    id: `finding-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    detectedAt: new Date().toISOString(),
  });
  audit.updatedAt = new Date().toISOString();
  saveAudits(audits);
  return audit;
}

export function completeAudit(auditId: string): SecurityAudit | undefined {
  const audits = loadAudits();
  const audit = audits.find((a) => a.id === auditId);
  if (!audit) return undefined;
  audit.status = 'COMPLETE';
  audit.updatedAt = new Date().toISOString();
  audit.score = calculateScore(audit);
  saveAudits(audits);
  return audit;
}

export function deleteAudit(auditId: string): void {
  const audits = loadAudits().filter((a) => a.id !== auditId);
  saveAudits(audits);
}

export function calculateScore(audit: SecurityAudit): number {
  const applicable = audit.controls.filter((c) => c.status !== 'NOT_APPLICABLE');
  if (applicable.length === 0) return 0;
  const implemented = applicable.filter((c) => c.status === 'IMPLEMENTED').length;
  const partial = applicable.filter((c) => c.status === 'PARTIAL').length;
  return Math.round(((implemented + partial * 0.5) / applicable.length) * 100);
}

export function getDomainScore(audit: SecurityAudit, domain: AuditDomain): number {
  const controls = audit.controls.filter((c) => c.domain === domain && c.status !== 'NOT_APPLICABLE');
  if (controls.length === 0) return 0;
  const implemented = controls.filter((c) => c.status === 'IMPLEMENTED').length;
  const partial = controls.filter((c) => c.status === 'PARTIAL').length;
  return Math.round(((implemented + partial * 0.5) / controls.length) * 100);
}

export const AUDIT_DOMAINS: { key: AuditDomain; label: string }[] = [
  { key: 'DATA_INVENTORY', label: 'Data Inventory' },
  { key: 'ACCESS_CONTROLS', label: 'Access Controls' },
  { key: 'ENCRYPTION', label: 'Encryption' },
  { key: 'INCIDENT_RESPONSE', label: 'Incident Response' },
  { key: 'VENDOR_MANAGEMENT', label: 'Vendor Management' },
  { key: 'EMPLOYEE_TRAINING', label: 'Employee Training' },
  { key: 'DATA_RETENTION', label: 'Data Retention' },
  { key: 'BREACH_NOTIFICATION', label: 'Breach Notification' },
];
